'use client';

import { cn } from '@core/utils';

import { LogoContainer } from './LogoContainer';
import type { Size } from './types';

const E_PRIVACY_CERT_NUM = {
  b2b: '2026-E-R011',
  b2c: '2026-E-R012',
} as const;

interface CertificationBadgeProps {
  size?: Size;
  isHideCSAP?: boolean;
  isHideISMSP?: boolean;
  isHideERIVACY?: boolean;
  ePrivacyVariant?: 'b2b' | 'b2c';
  className?: string;
}

export const CertificationBadge = ({
  size = 40,
  isHideCSAP,
  isHideISMSP,
  isHideERIVACY,
  ePrivacyVariant = 'b2c',
  className,
}: CertificationBadgeProps) => {
  return (
    <ul className={cn('flex items-center gap-8', className)}>
      {isHideCSAP ? null : (
        <li className="flex items-start gap-8">
          <LogoContainer size={size}>
            <img
              alt="CSAP 로고"
              className={cn({
                'size-40': size === 40,
                'size-50': size === 50,
                'size-60': size === 60,
                'size-80': size === 80,
              })}
              src="https://cdn.welfarehello.com/common/footer/csap-150px.png"
              title="CSAP"
              width={80}
              height={80}
            />
          </LogoContainer>
          <p className="font-400 text-Gray-600 typo-caption2">
            <span className="font-600 text-Gray-700">CSAP</span>
            <br />
            [인증범위] 공공기관용 웰로비즈(SaaS 표준등급)
            <br />
            [유효기간] 2025-09-30 ~ 2030-09-29
          </p>
        </li>
      )}
      {isHideISMSP ? null : (
        <li className="flex items-start gap-8">
          <LogoContainer>
            <img
              className={cn({
                'size-40': size === 40,
                'size-50': size === 50,
                'size-60': size === 60,
                'size-80': size === 80,
              })}
              alt="ISMS-P 로고"
              src="https://cdn.welfarehello.com/common/footer/ismsp-150px.png"
              width={80}
              height={80}
            />
          </LogoContainer>
          <p className="text-start font-400 text-Gray-600 typo-caption2">
            <span className="font-600 text-Gray-700">ISMS-P</span>
            <br />
            [인증범위] 웰로 서비스 및 웰로비즈 서비스
            <br />
            [유효기간] 2024-11-20 ~ 2027-11-19
          </p>
        </li>
      )}
      {isHideERIVACY ? null : (
        <li className="flex items-start gap-8">
          <LogoContainer>
            <button
              type="button"
              onClick={() => {
                window.open(
                  `https://www.eprivacy.or.kr/front/certifiedSiteMark/certifiedSiteMarkPopup.do?certCmd=E&certNum=${E_PRIVACY_CERT_NUM[ePrivacyVariant]}`,
                  'seal',
                  'width=527,height=720,location=no,status=no,scrollbars=no',
                );
              }}
            >
              <img
                alt="ePRIVACY"
                className={cn({
                  'size-40': size === 40,
                  'size-50': size === 50,
                  'size-60': size === 60,
                  'size-80': size === 80,
                })}
                width={80}
                height={80}
                src="https://www.eprivacy.or.kr/images/mng/sub/mark1_eprivacy.png"
                title="ePRIVACY"
              />
            </button>
          </LogoContainer>
          <p className="font-400 text-Gray-600 typo-caption2">
            <span className="font-600 text-Gray-700">ePRIVACY</span>
            <br />
            [인증범위] 웰로 서비스 및 웰로비즈 서비스
            <br />
            [유효기간] 2026-04-01 ~ 2027-03-31
          </p>
        </li>
      )}
      <li className="flex items-start gap-8">
        <LogoContainer>
          <img
            alt="ISO27701 로고"
            className={cn({
              'size-40': size === 40,
              'size-50': size === 50,
              'size-60': size === 60,
              'size-80': size === 80,
            })}
            src="https://cdn.welfarehello.com/common/footer/igc-150px.png"
            title="ISO27701"
            width={80}
            height={80}
          />
        </LogoContainer>
        <p className="font-400 text-Gray-600 typo-caption2">
          <span className="font-600 text-Gray-700">
            ISO/IEC 27001:2022
            <br />
            ISO/IEC 27701:2019
          </span>
          <br />
          [인증범위] 웰로 서비스 및 웰로비즈 서비스
          <br />
          [유효기간] 2024-12-24 ~ 2027-12-23
        </p>
      </li>
    </ul>
  );
};
